import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, UserPlus, Image as ImageIcon, HelpCircle, TrendingUp, Star, Check, Heart, MessageCircle, ShieldCheck, AtSign } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";

const ICONS = {
  friend_request: UserPlus,
  friend_accept: Check,
  follow: UserPlus,
  photo_received: ImageIcon,
  photo_request: HelpCircle,
  sale: TrendingUp,
  subscription: Star,
  like: Heart,
  comment: MessageCircle,
  mention: AtSign,
  kyc: ShieldCheck,
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const seenRef = useRef(null); // most recent notification id we've already shown
  const firstRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      const list = data.notifications || [];
      // Live toast for brand-new notifications (skip the very first load).
      if (!firstRef.current && list.length) {
        const newest = list[0];
        if (newest.id !== seenRef.current && !newest.read) {
          toast(newest.message, { description: "Nouvelle notification", duration: 5000 });
        }
      }
      if (list.length) seenRef.current = list[0].id;
      firstRef.current = false;
      setItems(list);
      setUnread(data.unread);
    } catch (e) {}
  }, []);

  useEffect(() => {
    load();
    // Temps réel : flux SSE (EventSource). Repli automatique sur l'interrogation périodique
    // si le flux est indisponible (proxy, réseau mobile...).
    let es = null;
    let poll = null;
    let stopped = false;
    const startPolling = () => {
      if (!poll) poll = setInterval(load, 15000);
    };
    const connect = () => {
      const token = localStorage.getItem("verrouille_token");
      if (!token || typeof window.EventSource === "undefined") { startPolling(); return; }
      es = new window.EventSource(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/stream?auth=${encodeURIComponent(token)}`);
      es.addEventListener("ready", () => {
        if (poll) { clearInterval(poll); poll = null; }
      });
      es.addEventListener("notification", (ev) => {
        try {
          const { notification } = JSON.parse(ev.data);
          if (notification) {
            seenRef.current = notification.id;
            setItems((prev) => [notification, ...prev].slice(0, 50));
            setUnread((u) => u + 1);
            toast(notification.message, { description: "Nouvelle notification", duration: 5000 });
            // Diffusion interne : les panneaux (ex. Admin → Historique) se rafraîchissent sans rechargement
            window.dispatchEvent(new CustomEvent("mihod:notification", { detail: notification }));
          }
        } catch (e) {}
      });
      es.onerror = () => {
        // EventSource se reconnecte seul ; on assure la continuité par un polling léger
        startPolling();
        if (es && es.readyState === 2 && !stopped) {
          setTimeout(connect, 10000);
        }
      };
    };
    connect();
    return () => {
      stopped = true;
      if (es) es.close();
      if (poll) clearInterval(poll);
    };
  }, [load]);

  const openChange = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      try {
        await api.post("/notifications/read-all");
        setUnread(0);
      } catch (e) {}
    }
  };

  const clickItem = (n) => {
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={openChange}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 grid place-items-center rounded-xl hover:bg-secondary transition-colors" data-testid="notification-bell" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold grid place-items-center neon-glow-red-sm" data-testid="notification-unread-count">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl">
        <div className="px-4 py-3 border-b border-border font-medium font-display">Notifications</div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">Aucune notification.</p>
          ) : (
            items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button key={n.id} onClick={() => clickItem(n)} data-testid="notification-item"
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-secondary transition-colors ${n.read ? "" : "bg-primary/5"}`}>
                  <span className="h-8 w-8 rounded-lg bg-secondary grid place-items-center shrink-0">
                    <Icon className="h-4 w-4 text-primary" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm leading-snug">{n.message}</span>
                    <span className="block text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</span>
                  </span>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-primary mt-1 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
